import { getWaypointIcon, mapNominatimTypeToWaypointType } from '@/services/waypointIconService'
import type { WorkspacePOI } from '@/types/poi'

import type { Component } from 'vue'

export function getPOIIcon(type?: string): Component {
  return getWaypointIcon(type)
}

/**
 * Marker colour per RouteCamp POI type, matched to the **route.camp basemap's own POI colours** (the
 * `text-color` of each `pois_*` style layer) so an addable/saved POI marker is the same colour as the
 * basemap symbol for that category — maximum consistency. The basemap groups POIs into six families:
 *   food #CB6704 · shopping #1A8CBD · water+transport #315BCF · nature #20834D ·
 *   services/huts/amenities #6A5B8F · tourism/culture #EF56BA.
 * Unknown/untyped POIs keep the familiar RouteCamp orange.
 */
export const DEFAULT_POI_COLOR = '#f97316'

const POI_TYPE_COLORS: Record<string, string> = {
  // food family (orange) — restaurant / cafe / bar / fast food
  food: '#CB6704',
  fuel: '#CB6704',
  // shopping family (blue)
  shopping: '#1A8CBD',
  // water + transport family (indigo-blue)
  water: '#315BCF',
  valley: '#315BCF',
  transport: '#315BCF',
  train: '#315BCF',
  station: '#315BCF',
  railway: '#315BCF',
  bus: '#315BCF',
  ferry: '#315BCF',
  airport: '#315BCF',
  // nature family (green) — peaks / parks / forests / viewpoints / beaches
  summit: '#20834D',
  forest: '#20834D',
  // tourism / culture family (pink) — attractions / museums / artwork
  landmark: '#EF56BA',
  church: '#EF56BA',
  // services / huts / amenities family (muted purple)
  shelter: '#6A5B8F',
  accommodation: '#6A5B8F',
  information: '#6A5B8F',
  parking: '#6A5B8F',
  building: '#6A5B8F',
  car: '#6A5B8F',
  generic: '#6A5B8F',
  // health / hazard (red) — RouteCamp-only types, no basemap equivalent
  danger: '#dc2626',
  hospital: '#dc2626',
}

export function getPoiColor(type?: string): string {
  if (!type) return DEFAULT_POI_COLOR
  return POI_TYPE_COLORS[type.toLowerCase()] ?? DEFAULT_POI_COLOR
}

export function mapSearchResultToPOIType(type?: string, category?: string): string | undefined {
  return mapNominatimTypeToWaypointType(type, category)
}

export function poiSnapshot(poi: WorkspacePOI): NonNullable<WorkspacePOI['_previousState']> {
  return {
    clientId: poi.clientId,
    workspaceClientId: poi.workspaceClientId,
    sourceWorkspaceClientId: poi.sourceWorkspaceClientId,
    isPinned: poi.isPinned,
    canEdit: poi.canEdit,
    lat: poi.lat,
    lon: poi.lon,
    ele: poi.ele,
    name: poi.name,
    description: poi.description,
    time: poi.time,
    type: poi.type,
  }
}
